import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  User,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  limit
} from 'firebase/firestore';
import { auth, db } from '../firebase';

interface AuthContextType {
  currentUser: User | null;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user?.email); // Debug log
      if (!authInitialized) {
        setAuthInitialized(true);
      }

      setCurrentUser(user);
      
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const isUserAdmin = userDoc.data()?.role === 'admin';
          console.log('Is user admin:', isUserAdmin); // Debug log
          setIsAdmin(isUserAdmin);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [authInitialized]);

  const signIn = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists() || userDoc.data()?.role !== 'admin') {
        await firebaseSignOut(auth);
        throw new Error('Nem rendelkezik admin jogosultsággal!');
      }
    } catch (error) {
      await firebaseSignOut(auth);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      const usersSnapshot = await getDocs(query(
        collection(db, 'users'),
        where('role', '==', 'admin'),
        limit(1)
      ));

      if (!usersSnapshot.empty) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists() || userDoc.data()?.role !== 'admin') {
          await firebaseSignOut(auth);
          throw new Error('Már van aktív admin felhasználó. Köszönjük érdeklődését!');
        }
        setIsAdmin(true);
        return;
      }
      
      await setDoc(doc(db, 'users', user.uid), {
        role: 'admin',
        email: user.email,
        name: user.displayName || 'Admin Felhasználó',
        createdAt: serverTimestamp(),
        photoURL: user.photoURL || null
      });
      setIsAdmin(true);
    } catch (error) {
      await firebaseSignOut(auth);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setIsAdmin(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    currentUser,
    isAdmin,
    signIn,
    signInWithGoogle,
    signOut,
    loading
  };

  if (!authInitialized) {
    return null;
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 